import { FC, useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Menu } from 'antd';
import { TbDotsVertical, TbEdit, TbEye, TbMessageCircle, TbUserMinus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { VirtualizedList as List } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { SmallTag } from '@/shared/components/tags';
import { useCollaboratorStore } from '@/stores/database/entities/00-RBAC/99-Collaborators';
import { CollaboratorPermissionType } from '@/types';

interface InvitedMemberListItemProps {
    resourceType: any;
    resource: any;
    collaborator: any;
    collaborators: any[];
    canManage: boolean;
}

const InvitedMemberListItem: FC<InvitedMemberListItemProps> = ({
    resourceType,
    resource,
    collaborator,
    collaborators,
    canManage,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const resourceId = resource?.id;

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();

    const isOwner = collaborator?.permission === CollaboratorPermissionType.OWNER;
    const disabled = !canManage || isOwner;

    const { updateCollaboratorPermission, removeCollaborator } = useCollaboratorStore();

    const handlePermissionUpdate = async (permissionKey: any) => {
        if (!canManage || !resourceId) {
            return;
        }

        // try {
        //     await authorizedAxiosInstance
        //         .put(`/api/${resourceType}/${resourceId}/collaborators/${collaborator?.id}/permission`, {
        //             permission: permissionKey,
        //         })
        //         .then((res: AxiosResponse) => {
        //             if (res?.status === 200) {
        //                 setPermission(res?.data?.permission);

        //                 toast('Visibility Updated', {
        //                     icon: permission === 'Private' ? <TbLock size='1rem' /> : <TbLink size='1rem' />,
        //                     description: (
        //                         <>
        //                             {collaborator?.member?.user?.firstName} {collaborator?.member?.user?.lastName} (
        //                             {res?.data?.permission})
        //                         </>
        //                     ),
        //                 });
        //             }
        //         });
        // } catch (error) {
        //     captureException(error);
        // }

        await updateCollaboratorPermission(resourceType, resourceId, collaborator.id, permissionKey as any);
    };

    const handleRemoveCollaboratorFromResource = async () => {
        if (!canManage || !resourceId) {
            return;
        }

        // try {
        //     await authorizedAxiosInstance
        //         .delete(`/api/${resourceType}/${resourceId}/collaborators/${collaborator?.id}`)
        //         .then((res: AxiosResponse) => {
        //             if (res.status === 200) {
        //                 toast(`Removed from ${resourceType && capitalCase(resourceType)}`, {
        //                     icon: <TbUserMinus size='1rem' />,
        //                     description: collaborator?.member?.user?.emailAddress,
        //                 });

        //                 const filtered = collaborators?.filter((c) => c?.id !== collaborator?.id);

        //                 setCollaborators(filtered);
        //             }
        //         });
        // } catch (error) {
        //     captureException(error);
        // }

        await removeCollaborator(resourceType, resourceId, collaborator.id);
    };

    return (
        <List.Item key={collaborator?.id}>
            <List.Item.Meta
                avatar={<Avatar src={collaborator?.member?.user?.avatar} style={{ verticalAlign: '-1.5rem' }} />}
                title={
                    <>
                        {collaborator?.member?.user?.firstName} {collaborator?.member?.user?.lastName}
                        {collaborator?.permission === 'Owner' && <SmallTag message={<>Owner</>} />}
                    </>
                }
                description={`${collaborator?.member?.user?.emailAddress}`}
            />
            <Dropdown
                disabled={disabled}
                placement='bottomRight'
                trigger={['click']}
                popupRender={() => (
                    <DropdownMenuPanel
                        selectedKeys={[collaborator?.permission]}
                        onClick={(e) => {
                            handlePermissionUpdate(e.key);
                        }}
                    >
                        <div style={{ padding: '0 0.75rem', marginBottom: '0.325rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>Collaborator Permission</small>
                        </div>
                        <Menu.Item key='View'>
                            <TbEye size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />
                            View
                            <br />
                            <small style={{ color: token.colorTextSecondary }}>
                                Ability to view a resouce and its contents.
                            </small>
                        </Menu.Item>
                        <Menu.Item key='Comment'>
                            <TbMessageCircle
                                size='1rem'
                                style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }}
                            />
                            Comment
                            <br />
                            <small style={{ color: token.colorTextSecondary }}>
                                View permissions + the ability to converse with other collaborators in the resource.
                            </small>
                        </Menu.Item>
                        <Menu.Item key='Edit'>
                            <TbEdit size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />
                            Edit
                            <br />
                            <small style={{ color: token.colorTextSecondary }}>
                                Ability to make edits to the resource and its contents.
                            </small>
                        </Menu.Item>
                    </DropdownMenuPanel>
                )}
            >
                <DropdownButton size='small' style={{ marginRight: '0.75rem' }} type='dashed' disabled={disabled}>
                    {collaborator?.permission}
                </DropdownButton>
            </Dropdown>
            <Dropdown
                placement='bottomRight'
                trigger={['click']}
                popupRender={() => (
                    <DropdownMenuPanel>
                        <Menu.Item
                            danger
                            key='remove'
                            disabled={disabled}
                            icon={<TbUserMinus size='1rem' />}
                            onClick={handleRemoveCollaboratorFromResource}
                        >
                            Remove
                        </Menu.Item>
                    </DropdownMenuPanel>
                )}
            >
                <Button
                    size='small'
                    disabled={disabled}
                    type='text'
                    icon={<TbDotsVertical size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
                />
            </Dropdown>
        </List.Item>
    );
};

export default InvitedMemberListItem;
